interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  customParameters?: Record<string, string | number>;
}

interface UserProperties extends Record<string, string | number> {
  userId: number;
  orgId: number;
  orgName: string;
  orgHostname: string;
}

class AnalyticsService {
  private static isEnabled = import.meta.env.PROD;

  private static trackingId = import.meta.env.VITE_GA_TRACKING_ID;

  private static userId: number;

  static init() {
    if (!this.isEnabled || !this.trackingId) {
      console.warn('Analytics is not enabled');
      return;
    }
    if (typeof window.gtag === 'undefined') {
      console.warn('Google Analytics is not loaded');
      return;
    }

    window.gtag('config', this.trackingId, { send_page_view: false });
  }

  static trackEvent(event: AnalyticsEvent) {
    if (!this.isEnabled || typeof window.gtag === 'undefined') {
      console.warn(
        'Analytics is not enabled or Google Analytics is not loaded',
      );
      return;
    }

    try {
      window.gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        ...event.customParameters,
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  static trackLogin(properties: UserProperties) {
    this.userId = properties.userId;
    this.trackEvent({
      action: 'identify',
      category: 'user',
      customParameters: {
        user_id: this.userId,
        org_id: properties.orgId,
        org_name: properties.orgName,
        org_hostname: window.location.hostname,
        page_title: 'Monitoring',
        page_name: 'Monitoring',
      },
    });
    window.gtag('config', this.trackingId, {
      user_id: this.userId,
    });
  }

  static trackLogout(properties: UserProperties) {
    this.trackEvent({
      action: 'logout',
      category: 'user',
      customParameters: {
        org_id: properties.orgId,
        org_name: properties.orgName,
        org_hostname: window.location.hostname,
        page_title: 'Login',
        page_name: 'Login',
      },
    });
  }

  static trackPageView(pageName: string, section: string) {
    this.trackEvent({
      action: 'page_view',
      category: 'page',
      customParameters: {
        page_name: pageName,
        page_title: section,
        page_path: window.location.pathname,
      },
    });
    window.gtag('config', this.trackingId, {
      page_path: window.location.pathname,
      page_title: section,
      page_location: window.location.href,
      user_id: this.userId,
    });
  }

  static trackPageDuration(
    pageName: string,
    section: string,
    duration: number,
  ) {
    this.trackEvent({
      action: 'page_duration',
      category: 'engagement',
      label: pageName,
      value: Math.round(duration / 1000),
      customParameters: {
        page_title: section,
        page_name: pageName,
      },
    });
  }

  static trackSPANavigation(from: string, to: string) {
    this.trackEvent({
      action: 'spa_navigation',
      category: 'navigation',
      label: `${from} -> ${to}`,
      customParameters: {
        from_page: from,
        to_page: to,
      },
    });
  }
}

export { AnalyticsService };
